import { useAppSelector } from '@store';
import { useParams } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonPseudo,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { CustomerStatus, OrganizationType } from '../../types';
import { CustomerDocumentStatus } from '../types';
import useDocumentInfo from '../hooks/use-documents-info';
import DocumentInfoContent from '../../../../components/common/documents/info';

import cn from '../styles.module.css';

const DocumentInfo = ({ customerType }: { customerType: OrganizationType }) => {
  const { info, allowedCategories } = useAppSelector(
    (state) => state.customerDocuments,
  );
  const { customersTransactionTypes } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { activePermissions } = useAppSelector((state) => state.root);
  const {
    users: { users },
  } = useAppSelector((state) => state.userList);

  const { documentId, customerId } = useParams();

  const {
    documentName,
    setDeletePopupVisible,
    onDelete,
    deleteInProgress,
    deletePopupVisible,
    panelIsOpen,
    handleClose,
    documentRenameInProgress,
    onRename,
    statusChangeInProgress,
    selectedStatus,
    setSelectedStatus,
    changeCustomerDocumentsStatus,
    isLoading,
    moveInProgress,
    changeCustomerDocumentsCategoryAction,
    imgLink,
    fileIsLoading,
    isFetching,
    setDocumentName,
    commentInProgress,
    addCommentAction,
    onDownload,
    textareaRef,
    onChangeDocumentExpDate,
    changeDateError,
    updateInProgress,
    documentTransactionType,
    nameError,
    errors,
  } = useDocumentInfo({
    customerType,
    documentId,
    customerId,
    info,
    customersTransactionTypes,
  });

  const organizationStatus =
    customerType === OrganizationType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  return (
    <>
      <Popup
        title={`Delete ${documentName}`}
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title={documentName || ''}
        sidepanelExpandedKey="customer-document"
        footer={
          <>
            {((customerType === OrganizationType.INDIVIDUAL &&
              activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
              )) ||
              (customerType === OrganizationType.CORPORATE &&
                activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                ))) &&
              info &&
              organizationStatus !== CustomerStatus.CLOSED && (
                <>
                  <ButtonPrimary
                    text={documentRenameInProgress ? 'Loading...' : 'Save'}
                    disabled={
                      info.name === documentName || documentRenameInProgress
                    }
                    onClick={onRename}
                    bold
                  />
                  {info.status !== CustomerDocumentStatus.ACCEPTED &&
                    info.status !== CustomerDocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === CustomerDocumentStatus.ACCEPTED
                            ? 'Loading...'
                            : 'Accept'
                        }
                        onClick={() => {
                          setSelectedStatus(CustomerDocumentStatus.ACCEPTED);

                          changeCustomerDocumentsStatus({
                            id: customerId as string,
                            body: {
                              ids: [info?.id],
                              status: CustomerDocumentStatus.ACCEPTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                  {info.status !== CustomerDocumentStatus.REJECTED &&
                    info.status !== CustomerDocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === CustomerDocumentStatus.REJECTED
                            ? 'Loading...'
                            : 'Reject'
                        }
                        className={cn.rejectStatusButton}
                        onClick={() => {
                          setSelectedStatus(CustomerDocumentStatus.REJECTED);

                          changeCustomerDocumentsStatus({
                            id: customerId as string,
                            body: {
                              ids: [info?.id],
                              status: CustomerDocumentStatus.REJECTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                </>
              )}
            <ButtonPseudo
              text="Close"
              onClick={handleClose}
              className="button-white"
              bold
            />
          </>
        }
      >
        <DocumentInfoContent
          isLoading={isLoading}
          info={info}
          setDeletePopupVisible={setDeletePopupVisible}
          moveInProgress={moveInProgress}
          onMove={(data) => changeCustomerDocumentsCategoryAction(data)}
          imgLink={imgLink}
          fileIsLoading={fileIsLoading}
          downloadInProgress={isFetching}
          commentsVisible={activePermissions?.includes(
            PermissionEnum.DOCUMENTS_COMMENTS_ACTION,
          )}
          updateAllowed={activePermissions?.includes(
            PermissionEnum.DOCUMENTS_UPDATE_ACTION,
          )}
          documentName={documentName}
          setDocumentName={setDocumentName}
          commentInProgress={commentInProgress}
          sendComment={(text) => addCommentAction(text)}
          customerType={customerType}
          deleteIsVisible={
            (customerType === OrganizationType.INDIVIDUAL &&
              activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
              )) ||
            (customerType === OrganizationType.CORPORATE &&
              activePermissions?.includes(
                PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
              ))
          }
          onDownload={onDownload}
          textareaRef={textareaRef}
          onChangeDocumentExpDate={onChangeDocumentExpDate}
          changeDateError={changeDateError}
          updateInProgress={updateInProgress}
          documentTransactionType={documentTransactionType}
          allowedCategories={allowedCategories}
          nameError={nameError || (errors?.name?.message as string)}
          renameAvailable={
            customerType === OrganizationType.INDIVIDUAL
              ? activePermissions?.includes(
                  PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
                )
              : activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
                )
          }
          status={organizationStatus}
          users={users}
        />
      </SidePanel>
    </>
  );
};

export default DocumentInfo;
